import { useQuery } from '@tanstack/react-query'
import {
	Archive,
	ArrowDown,
	ArrowUp,
	CalendarDays,
	CheckCircle2,
	Circle,
	Clock3,
	Copy,
	Dumbbell,
	Eye,
	GripVertical,
	ImageIcon,
	Plus,
	RefreshCw,
	Save,
	Send,
	Trash2,
	Undo2,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import { exerciseKeys, type Exercise } from '@entities/exercise'
import {
	createBuilderDay,
	createBuilderExercise,
	createBuilderWeek,
	duplicateBuilderDay,
	duplicateBuilderWeek,
	getProgramPublicationChecklist,
	getProgramStructure,
	moveArrayItem,
	normalizeProgramBuilder,
	programBuilderDuration,
	programKeys,
	programLifecycleClass,
	programLifecycleLabel,
	programValidationPathLabel,
	validateProgramBuilder,
	type Program,
	type ProgramBuilderDay,
	type ProgramBuilderExercise,
	type ProgramBuilderState,
} from '@entities/program'
import { unwrapApiResponse } from '@shared/api'
import { ConfirmDialog } from '@shared/ui/confirm-dialog'
import { InlineNotice } from '@shared/ui/page'
import { useToast } from '@shared/ui/toast'
import { programLifecycleMeta, useProgramWorkflow, type ProgramLifecycleAction } from './program-workflow'
import { ProgramCoverPicker, ProgramExercisePicker } from './resource-pickers'

const formatDate = (value?: string) => value
	? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
	: '—'

const formatDuration = (seconds: number) => {
	const hours = Math.floor(seconds / 3600)
	const minutes = Math.floor((seconds % 3600) / 60)
	const rest = seconds % 60
	return [hours ? `${hours} ч` : '', minutes ? `${minutes} мин` : '', rest || (!hours && !minutes) ? `${rest} сек` : ''].filter(Boolean).join(' ')
}

const numericValue = (value: string) => value === '' ? undefined : Math.max(0, Math.trunc(Number(value)))

const NumberField = ({
	label,
	value,
	onChange,
	disabled,
}: {
	label: string
	value?: number
	onChange: (value?: number) => void
	disabled: boolean
}) => (
	<label className='space-y-1 text-xs font-medium'>
		<span>{label}</span>
		<Input type='number' min={0} value={value ?? ''} onChange={event => onChange(numericValue(event.target.value))} disabled={disabled} />
	</label>
)

const ExerciseName = ({ exerciseId }: { exerciseId: string }) => {
	const api = useAdminApi()
	const query = useQuery({
		queryKey: exerciseKeys.detail(exerciseId),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/exercises/{exercise_id}', {
			params: { path: { exercise_id: exerciseId } },
		})).data,
		staleTime: 60_000,
	})
	return <span>{query.data?.name ?? exerciseId}</span>
}

const ExerciseEditor = ({
	exercise,
	index,
	count,
	disabled,
	onPatch,
	onMove,
	onRemove,
	onDragStart,
	onDrop,
}: {
	exercise: ProgramBuilderExercise
	index: number
	count: number
	disabled: boolean
	onPatch: (patch: Partial<ProgramBuilderExercise>) => void
	onMove: (direction: -1 | 1) => void
	onRemove: () => void
	onDragStart: () => void
	onDrop: () => void
}) => (
	<div
		draggable={!disabled}
		onDragStart={event => { event.stopPropagation(); onDragStart() }}
		onDragOver={event => { if (!disabled) event.preventDefault() }}
		onDrop={event => { event.preventDefault(); onDrop() }}
		className='rounded-xl border bg-background p-3'>
		<div className='flex items-center gap-2'>
			<GripVertical className='h-4 w-4 text-muted-foreground' aria-hidden='true' />
			<Dumbbell className='h-4 w-4 text-primary' aria-hidden='true' />
			<p className='min-w-0 flex-1 truncate font-medium'><ExerciseName exerciseId={exercise.exercise_id} /></p>
			<div className='flex gap-1'>
				<Button type='button' size='icon' variant='ghost' disabled={disabled || index === 0} onClick={() => onMove(-1)} aria-label='Переместить упражнение выше'><ArrowUp className='h-4 w-4' /></Button>
				<Button type='button' size='icon' variant='ghost' disabled={disabled || index === count - 1} onClick={() => onMove(1)} aria-label='Переместить упражнение ниже'><ArrowDown className='h-4 w-4' /></Button>
				<Button type='button' size='icon' variant='ghost' disabled={disabled} onClick={onRemove} aria-label='Удалить упражнение'><Trash2 className='h-4 w-4 text-red-600' /></Button>
			</div>
		</div>
		<div className='mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
			<NumberField label='Подходы' value={exercise.sets} onChange={sets => onPatch({ sets })} disabled={disabled} />
			<NumberField label='Повторы' value={exercise.repetitions} onChange={repetitions => onPatch({ repetitions })} disabled={disabled} />
			<NumberField label='Длительность, сек.' value={exercise.duration_seconds} onChange={duration_seconds => onPatch({ duration_seconds })} disabled={disabled} />
			<NumberField label='Отдых, сек.' value={exercise.rest_seconds} onChange={rest_seconds => onPatch({ rest_seconds })} disabled={disabled} />
		</div>
		<div className='mt-3 grid gap-3 lg:grid-cols-2'>
			<label className='space-y-1 text-xs font-medium'>
				<span>Интенсивность</span>
				<Input value={exercise.intensity ?? ''} onChange={event => onPatch({ intensity: event.target.value })} disabled={disabled} placeholder='Например, RPE 7' />
			</label>
			<label className='space-y-1 text-xs font-medium'>
				<span>Заметки</span>
				<Input value={exercise.notes ?? ''} onChange={event => onPatch({ notes: event.target.value })} disabled={disabled} placeholder='Темп, техника, ограничения' />
			</label>
		</div>
	</div>
)

export const ProgramBuilder = ({ program, onBack }: { program: Program; onBack: () => void }) => {
	const api = useAdminApi()
	const { can } = useAuth()
	const { notify } = useToast()
	const {
		currentProgram,
		draft,
		setDraft,
		baselineState,
		etag,
		normalized,
		dirty,
		editable,
		restoredDraft,
		serverValidation,
		conflict,
		pendingAction,
		save,
		validate,
		lifecycle,
		clone,
		discardLocalDraft,
		resetCloneAttempt,
	} = useProgramWorkflow(program)
	const [previewOpen, setPreviewOpen] = useState(false)
	const [cloneOpen, setCloneOpen] = useState(false)
	const [cloneName, setCloneName] = useState(`${program.name} — копия`)
	const [lifecycleAction, setLifecycleAction] = useState<ProgramLifecycleAction | null>(null)
	const [coverOpen, setCoverOpen] = useState(false)
	const [exerciseTarget, setExerciseTarget] = useState<{ week: number; day: number } | null>(null)
	const [dragWeek, setDragWeek] = useState<number | null>(null)
	const [dragDay, setDragDay] = useState<{ week: number; day: number } | null>(null)
	const [dragExercise, setDragExercise] = useState<{ week: number; day: number; exercise: number } | null>(null)
	const [reorderAnnouncement, setReorderAnnouncement] = useState('')
	const issues = useMemo(() => validateProgramBuilder(normalized), [normalized])
	const duration = useMemo(() => programBuilderDuration(normalized), [normalized])
	const structure = getProgramStructure({ weeks: normalized.weeks })
	const checklist = useMemo(() => getProgramPublicationChecklist(normalized, { dirty, serverValidation }), [dirty, normalized, serverValidation])
	const publicationReady = checklist.filter(item => item.required).every(item => item.complete)
	const announceReorder = (message: string) => {
		setReorderAnnouncement('')
		requestAnimationFrame(() => setReorderAnnouncement(message))
	}

	type WeekState = ProgramBuilderState['weeks'][number]
	type WeekPatch = Partial<WeekState> | ((week: WeekState) => Partial<WeekState>)
	type DayPatch = Partial<ProgramBuilderDay> | ((day: ProgramBuilderDay) => Partial<ProgramBuilderDay>)
	type ExercisePatch = Partial<ProgramBuilderExercise> | ((exercise: ProgramBuilderExercise) => Partial<ProgramBuilderExercise>)
	const commit = (update: ProgramBuilderState | ((current: ProgramBuilderState) => ProgramBuilderState)) =>
		setDraft(current => normalizeProgramBuilder(typeof update === 'function' ? update(current) : update))
	const patchWeek = (weekIndex: number, update: WeekPatch) => commit(current => ({
		...current,
		weeks: current.weeks.map((week, index) => {
			if (index !== weekIndex) return week
			const patch = typeof update === 'function' ? update(week) : update
			return { ...week, ...patch }
		}),
	}))
	const patchDay = (weekIndex: number, dayIndex: number, update: DayPatch) => patchWeek(weekIndex, week => ({
		days: week.days.map((day, index) => {
			if (index !== dayIndex) return day
			const patch = typeof update === 'function' ? update(day) : update
			return { ...day, ...patch }
		}),
	}))
	const patchExercise = (weekIndex: number, dayIndex: number, exerciseIndex: number, update: ExercisePatch) => patchDay(weekIndex, dayIndex, day => ({
		exercises: day.exercises.map((exercise, index) => {
			if (index !== exerciseIndex) return exercise
			const patch = typeof update === 'function' ? update(exercise) : update
			return { ...exercise, ...patch }
		}),
	}))

	const addExercise = (exercise: Exercise) => {
		if (!exerciseTarget) return
		const defaults = {
			sets: exercise.default_sets,
			repetitions: exercise.default_reps,
			duration_seconds: exercise.default_duration,
			rest_seconds: exercise.default_rest_time,
		}
		const day = draft.weeks[exerciseTarget.week].days[exerciseTarget.day]
		if (day.exercises.some(item => item.exercise_id === exercise.id)) {
			notify({
				title: 'Упражнение уже добавлено',
				description: 'Одно упражнение нельзя добавить в тренировочный день дважды.',
				tone: 'error',
			})
			setExerciseTarget(null)
			return
		}
		patchDay(exerciseTarget.week, exerciseTarget.day, currentDay => ({
			exercises: [...currentDay.exercises, createBuilderExercise(exercise.id, defaults)],
			workout_template_id: undefined,
		}))
		setExerciseTarget(null)
	}

	return (
		<div className='space-y-6'>
			<p className='sr-only' role='status' aria-live='polite' aria-atomic='true'>{reorderAnnouncement}</p>
			{restoredDraft ? <div className='space-y-3'><InlineNotice title='Восстановлен локальный черновик' description='Несохранённые изменения из этой вкладки восстановлены поверх последней известной серверной версии.' tone='warning' /><Button type='button' variant='outline' onClick={discardLocalDraft}><Undo2 className='h-4 w-4' /> Сбросить локальный черновик</Button></div> : null}
			{conflict ? <InlineNotice title='Серверная версия изменилась' description={`Загружен актуальный ETag версии ${conflict.version}. Ваш локальный черновик сохранён; повторное сохранение применит его к новой версии.`} tone='warning' /> : null}
			{currentProgram.lifecycle !== 'DRAFT' ? <InlineNotice title='Редактирование заблокировано текущим статусом' description={`Программа находится в статусе «${programLifecycleLabel[currentProgram.lifecycle]}». Верните её в черновик, чтобы менять структуру.`} /> : null}
			<div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-5'>
				{[
					['Недель', structure.weeks],
					['Тренировок', structure.trainingDays],
					['Дней отдыха', structure.restDays],
					['Упражнений', structure.exercises],
					['Расчётное время', formatDuration(duration)],
				].map(([label, value]) => <Card key={label}><CardContent className='p-4'><p className='text-xl font-semibold'>{value}</p><p className='mt-1 text-xs text-muted-foreground'>{label}</p></CardContent></Card>)}
			</div>

			<div className='grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]'>
				<div className='space-y-4'>
					<Card>
						<CardContent className='space-y-3 p-4'>
							<div className='flex h-44 items-center justify-center overflow-hidden rounded-xl bg-primary/10 text-primary'>
								{draft.cover_asset_id ? <ProgramCoverAsset assetId={draft.cover_asset_id} /> : <ImageIcon className='h-9 w-9' />}
							</div>
							<Button type='button' variant='outline' className='w-full' disabled={!editable} onClick={() => setCoverOpen(true)}>Выбрать обложку</Button>
							<div className='flex items-center justify-between gap-3 border-t pt-3 text-sm'><span className='text-muted-foreground'>Статус</span><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${programLifecycleClass[currentProgram.lifecycle]}`}>{programLifecycleLabel[currentProgram.lifecycle]}</span></div>
							<div className='flex items-center justify-between gap-3 text-sm'><span className='text-muted-foreground'>Версия</span><span>v{currentProgram.version}</span></div>
							<div className='space-y-1 border-t pt-3 text-sm'><span className='text-muted-foreground'>ETag</span><p className='break-all font-mono text-xs'>{etag}</p></div>
							<div className='space-y-1 border-t pt-3 text-sm'><span className='text-muted-foreground'>Изменено</span><p>{formatDate(currentProgram.updated_at)}</p></div>
						</CardContent>
					</Card>
					<Card>
						<CardHeader><CardTitle className='text-base'>Публикация</CardTitle></CardHeader>
						<CardContent className='space-y-3'>
							{checklist.map(item => <div key={item.id} className='flex items-start gap-2 text-sm'>
								{item.complete ? <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-emerald-600' /> : <Circle className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />}
								<div><p className='font-medium'>{item.label}{item.required ? '' : ' · необязательно'}</p><p className='text-xs leading-5 text-muted-foreground'>{item.description}</p>{item.path && !item.complete ? <code className='mt-1 block text-[11px]'>{programValidationPathLabel(item.path)}</code> : null}</div>
							</div>)}
							{can('validateProgram') ? <Button type='button' variant='outline' className='w-full' disabled={dirty || issues.length > 0 || pendingAction !== null} onClick={() => validate.mutate()}><RefreshCw className='h-4 w-4' /> {pendingAction === 'validate' ? 'Проверка…' : 'Проверить на сервере'}</Button> : null}
						</CardContent>
					</Card>
				</div>

				<div className='space-y-5'>
					<Card>
						<CardHeader><CardTitle>Свойства программы</CardTitle></CardHeader>
						<CardContent className='space-y-4'>
							<label className='block space-y-2'><span className='text-sm font-medium'>Название</span><Input value={draft.name} onChange={event => setDraft(current => ({ ...current, name: event.target.value }))} disabled={!editable} /></label>
							<label className='block space-y-2'><span className='text-sm font-medium'>Описание</span><textarea value={draft.description ?? ''} onChange={event => setDraft(current => ({ ...current, description: event.target.value }))} disabled={!editable} rows={5} className='w-full rounded-md border bg-background px-3 py-2 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60' /></label>
						</CardContent>
					</Card>

					{issues.length ? <div role='alert' className='rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900'><p className='font-medium'>Исправьте структуру перед сохранением:</p><ul className='mt-2 space-y-2'>{issues.map(issue => <li key={`${issue.path}-${issue.message}`}><p>{issue.message}</p><code className='text-xs opacity-80'>{programValidationPathLabel(issue.path)}</code></li>)}</ul></div> : null}
					{serverValidation && !serverValidation.valid ? <div role='alert' className='rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900'><p className='font-medium'>Серверная проверка:</p><ul className='mt-2 space-y-2'>{serverValidation.issues.map((issue, index) => <li key={`${issue.code}-${issue.path}-${index}`}><p>{issue.message}</p><code className='text-xs opacity-80'>{programValidationPathLabel(issue.path)}</code></li>)}</ul></div> : null}

					<div className='space-y-4'>
						{normalized.weeks.map((week, weekIndex) => <Card
							key={week.client_id}
							draggable={editable}
							onDragStart={() => setDragWeek(weekIndex)}
							onDragOver={event => { if (editable) event.preventDefault() }}
							onDrop={event => { event.preventDefault(); if (dragWeek !== null) commit(current => ({ ...current, weeks: moveArrayItem(current.weeks, dragWeek, weekIndex) })); setDragWeek(null) }}>
							<CardHeader className='border-b'>
								<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
									<GripVertical className='h-5 w-5 text-muted-foreground' aria-hidden='true' />
									<CalendarDays className='h-5 w-5 text-primary' aria-hidden='true' />
									<div className='min-w-0 flex-1'><CardTitle className='text-lg'>Неделя {weekIndex + 1}</CardTitle><Input value={week.name ?? ''} onChange={event => patchWeek(weekIndex, { name: event.target.value })} disabled={!editable} placeholder='Название недели' className='mt-2' /></div>
									<div className='flex flex-wrap gap-1'>
										<Button type='button' size='icon' variant='ghost' disabled={!editable || weekIndex === 0} onClick={() => { commit(current => ({ ...current, weeks: moveArrayItem(current.weeks, weekIndex, weekIndex - 1) })); announceReorder(`Неделя ${weekIndex + 1} перемещена на позицию ${weekIndex}.`) }} aria-label='Переместить неделю выше'><ArrowUp className='h-4 w-4' /></Button>
										<Button type='button' size='icon' variant='ghost' disabled={!editable || weekIndex === normalized.weeks.length - 1} onClick={() => { commit(current => ({ ...current, weeks: moveArrayItem(current.weeks, weekIndex, weekIndex + 1) })); announceReorder(`Неделя ${weekIndex + 1} перемещена на позицию ${weekIndex + 2}.`) }} aria-label='Переместить неделю ниже'><ArrowDown className='h-4 w-4' /></Button>
										<Button type='button' size='icon' variant='ghost' disabled={!editable || normalized.weeks.length >= 104} onClick={() => commit(current => ({ ...current, weeks: [...current.weeks.slice(0, weekIndex + 1), duplicateBuilderWeek(current.weeks[weekIndex]), ...current.weeks.slice(weekIndex + 1)] }))} aria-label='Дублировать неделю'><Copy className='h-4 w-4' /></Button>
										<Button type='button' size='icon' variant='ghost' disabled={!editable || normalized.weeks.length === 1} onClick={() => commit(current => ({ ...current, weeks: current.weeks.filter((_, index) => index !== weekIndex) }))} aria-label='Удалить неделю'><Trash2 className='h-4 w-4 text-red-600' /></Button>
									</div>
								</div>
							</CardHeader>
							<CardContent className='space-y-4 p-4'>
								{week.days.map((day, dayIndex) => <div
									key={day.client_id}
									draggable={editable}
									onDragStart={event => { event.stopPropagation(); setDragDay({ week: weekIndex, day: dayIndex }) }}
									onDragOver={event => { if (editable) event.preventDefault() }}
									onDrop={event => { event.preventDefault(); event.stopPropagation(); if (dragDay?.week === weekIndex) patchWeek(weekIndex, currentWeek => ({ days: moveArrayItem(currentWeek.days, dragDay.day, dayIndex) })); setDragDay(null) }}
									className='rounded-xl border bg-muted/20 p-4'>
									<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
										<GripVertical className='h-4 w-4 text-muted-foreground' aria-hidden='true' />
										<div className='min-w-0 flex-1'><p className='font-medium'>День {dayIndex + 1}</p><Input value={day.name ?? ''} onChange={event => patchDay(weekIndex, dayIndex, { name: event.target.value })} disabled={!editable} placeholder='Название дня' className='mt-2' /></div>
										<label className='flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm'><input type='checkbox' checked={day.is_rest_day} onChange={event => patchDay(weekIndex, dayIndex, { is_rest_day: event.target.checked, workout_template_id: undefined, exercises: event.target.checked ? [] : day.exercises })} disabled={!editable} className='h-4 w-4 accent-primary' /> День отдыха</label>
										<div className='flex gap-1'>
											<Button type='button' size='icon' variant='ghost' disabled={!editable || dayIndex === 0} onClick={() => { patchWeek(weekIndex, currentWeek => ({ days: moveArrayItem(currentWeek.days, dayIndex, dayIndex - 1) })); announceReorder(`День ${dayIndex + 1} недели ${weekIndex + 1} перемещён на позицию ${dayIndex}.`) }} aria-label='Переместить день выше'><ArrowUp className='h-4 w-4' /></Button>
											<Button type='button' size='icon' variant='ghost' disabled={!editable || dayIndex === week.days.length - 1} onClick={() => { patchWeek(weekIndex, currentWeek => ({ days: moveArrayItem(currentWeek.days, dayIndex, dayIndex + 1) })); announceReorder(`День ${dayIndex + 1} недели ${weekIndex + 1} перемещён на позицию ${dayIndex + 2}.`) }} aria-label='Переместить день ниже'><ArrowDown className='h-4 w-4' /></Button>
											<Button type='button' size='icon' variant='ghost' disabled={!editable || week.days.length >= 7} onClick={() => patchWeek(weekIndex, currentWeek => ({ days: [...currentWeek.days.slice(0, dayIndex + 1), duplicateBuilderDay(currentWeek.days[dayIndex]), ...currentWeek.days.slice(dayIndex + 1)] }))} aria-label='Дублировать день'><Copy className='h-4 w-4' /></Button>
											<Button type='button' size='icon' variant='ghost' disabled={!editable || week.days.length === 1} onClick={() => patchWeek(weekIndex, currentWeek => ({ days: currentWeek.days.filter((_, index) => index !== dayIndex) }))} aria-label='Удалить день'><Trash2 className='h-4 w-4 text-red-600' /></Button>
										</div>
									</div>
									{day.is_rest_day ? <p className='mt-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-800'>В выходной день упражнения и шаблон тренировки не отправляются.</p> : <div className='mt-4 space-y-3'>
										{day.exercises.map((exercise, exerciseIndex) => <ExerciseEditor
											key={exercise.client_id}
											exercise={exercise}
											index={exerciseIndex}
											count={day.exercises.length}
											disabled={!editable}
											onPatch={patch => patchExercise(weekIndex, dayIndex, exerciseIndex, patch)}
											onMove={direction => { patchDay(weekIndex, dayIndex, currentDay => ({ exercises: moveArrayItem(currentDay.exercises, exerciseIndex, exerciseIndex + direction) })); announceReorder(`Упражнение перемещено на позицию ${exerciseIndex + direction + 1} в дне ${dayIndex + 1} недели ${weekIndex + 1}.`) }}
											onRemove={() => patchDay(weekIndex, dayIndex, currentDay => ({ exercises: currentDay.exercises.filter((_, index) => index !== exerciseIndex) }))}
											onDragStart={() => setDragExercise({ week: weekIndex, day: dayIndex, exercise: exerciseIndex })}
											onDrop={() => { if (dragExercise?.week === weekIndex && dragExercise.day === dayIndex) patchDay(weekIndex, dayIndex, currentDay => ({ exercises: moveArrayItem(currentDay.exercises, dragExercise.exercise, exerciseIndex) })); setDragExercise(null) }}
										/>)}
										<Button type='button' variant='outline' disabled={!editable} onClick={() => setExerciseTarget({ week: weekIndex, day: dayIndex })}><Plus className='h-4 w-4' /> Добавить упражнение</Button>
									</div>}
								</div>)}
								<div className='flex flex-wrap gap-2'>
									<Button type='button' variant='outline' disabled={!editable || week.days.length >= 7} onClick={() => patchWeek(weekIndex, currentWeek => ({ days: [...currentWeek.days, createBuilderDay(false)] }))}><Plus className='h-4 w-4' /> Тренировочный день</Button>
									<Button type='button' variant='outline' disabled={!editable || week.days.length >= 7} onClick={() => patchWeek(weekIndex, currentWeek => ({ days: [...currentWeek.days, createBuilderDay(true)] }))}><Plus className='h-4 w-4' /> Выходной день</Button>
								</div>
							</CardContent>
						</Card>)}
					</div>

					<Button type='button' variant='outline' disabled={!editable || normalized.weeks.length >= 104} onClick={() => commit(current => ({ ...current, weeks: [...current.weeks, createBuilderWeek()] }))}><Plus className='h-4 w-4' /> Добавить неделю</Button>
				</div>
			</div>

			<div className='sticky bottom-4 z-20 flex flex-col gap-3 rounded-xl border bg-background/95 p-4 shadow-lg backdrop-blur lg:flex-row lg:items-center lg:justify-between'>
				<div className='flex items-center gap-2 text-sm'><Clock3 className='h-4 w-4 text-primary' /><span>{dirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены'}</span></div>
				<div className='flex flex-wrap gap-2'>
					<Button type='button' variant='outline' onClick={() => setPreviewOpen(true)}><Eye className='h-4 w-4' /> Предпросмотр</Button>
					{can('cloneProgram') ? <Button type='button' variant='outline' disabled={pendingAction !== null} onClick={() => { setCloneName(`${currentProgram.name} — копия`); setCloneOpen(true) }}><Copy className='h-4 w-4' /> Клонировать</Button> : null}
					{currentProgram.lifecycle === 'DRAFT' && can('publishProgram') ? <Button type='button' disabled={!publicationReady || pendingAction !== null} onClick={() => setLifecycleAction('publish')}><Send className='h-4 w-4' /> Опубликовать</Button> : null}
					{currentProgram.lifecycle === 'PUBLISHED' && can('unpublishProgram') ? <Button type='button' variant='outline' disabled={pendingAction !== null} onClick={() => setLifecycleAction('unpublish')}><Undo2 className='h-4 w-4' /> Снять с публикации</Button> : null}
					{currentProgram.lifecycle !== 'ARCHIVED' && can('archiveProgram') ? <Button type='button' variant='destructive' disabled={dirty || pendingAction !== null} onClick={() => setLifecycleAction('archive')}><Archive className='h-4 w-4' /> Архивировать</Button> : null}
					{currentProgram.lifecycle === 'ARCHIVED' && can('restoreProgram') ? <Button type='button' disabled={pendingAction !== null} onClick={() => setLifecycleAction('restore')}><RefreshCw className='h-4 w-4' /> Восстановить</Button> : null}
					<Button type='button' variant='outline' onClick={onBack}>К каталогу</Button>
					{editable ? <Button type='button' disabled={!dirty || issues.length > 0 || pendingAction !== null} onClick={() => save.mutate()}><Save className='h-4 w-4' /> {pendingAction === 'save' ? 'Сохранение…' : conflict ? 'Сохранить поверх новой версии' : 'Сохранить'}</Button> : null}
				</div>
			</div>

			<ProgramCoverPicker open={coverOpen} value={draft.cover_asset_id} onOpenChange={setCoverOpen} onChange={cover_asset_id => setDraft(current => ({ ...current, cover_asset_id }))} />
			<ProgramExercisePicker open={exerciseTarget !== null} onOpenChange={open => { if (!open) setExerciseTarget(null) }} onSelect={addExercise} />

			<Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
				<DialogContent className='max-h-[85vh] max-w-3xl overflow-y-auto'>
					<DialogHeader><DialogTitle>{normalized.name || 'Программа без названия'}</DialogTitle><DialogDescription>{normalized.description || 'Описание не заполнено.'}</DialogDescription></DialogHeader>
					<div className='grid gap-3 sm:grid-cols-4'>{[['Недель', structure.weeks], ['Тренировок', structure.trainingDays], ['Упражнений', structure.exercises], ['Время', formatDuration(duration)]].map(([label, value]) => <div key={label} className='rounded-lg border p-3'><p className='font-semibold'>{value}</p><p className='text-xs text-muted-foreground'>{label}</p></div>)}</div>
					<div className='space-y-4'>{normalized.weeks.map((week, weekIndex) => <div key={week.client_id} className='rounded-xl border p-4'><h3 className='font-semibold'>Неделя {weekIndex + 1}{week.name ? ` · ${week.name}` : ''}</h3><div className='mt-3 space-y-3'>{week.days.map((day, dayIndex) => <div key={day.client_id} className='rounded-lg bg-muted/30 p-3'><p className='font-medium'>День {dayIndex + 1}{day.name ? ` · ${day.name}` : ''}{day.is_rest_day ? ' · отдых' : ''}</p>{!day.is_rest_day ? <ol className='mt-2 list-decimal space-y-1 pl-5 text-sm'>{day.exercises.map(exercise => <li key={exercise.client_id}><ExerciseName exerciseId={exercise.exercise_id} /> · {exercise.sets ?? '—'}×{exercise.repetitions ?? '—'}</li>)}</ol> : null}</div>)}</div></div>)}</div>
					<DialogFooter><Button type='button' onClick={() => setPreviewOpen(false)}>Закрыть</Button></DialogFooter>
				</DialogContent>
			</Dialog>

			<ConfirmDialog open={cloneOpen} onOpenChange={setCloneOpen} title='Клонировать программу?' description='Будет создан независимый черновик с копией текущей сохранённой структуры.' confirmLabel='Создать копию' isPending={pendingAction === 'clone'} onConfirm={() => clone.mutate(cloneName)}>
				<div className='space-y-2'><Label htmlFor='program-clone-name'>Название копии</Label><Input id='program-clone-name' value={cloneName} maxLength={240} onChange={event => { if (clone.isError) { resetCloneAttempt(); clone.reset() }; setCloneName(event.target.value) }} /></div>
			</ConfirmDialog>

			<ConfirmDialog open={lifecycleAction !== null} onOpenChange={open => { if (!open) setLifecycleAction(null) }} title={lifecycleAction ? programLifecycleMeta[lifecycleAction].title : ''} description={lifecycleAction ? programLifecycleMeta[lifecycleAction].description : ''} confirmLabel={lifecycleAction ? programLifecycleMeta[lifecycleAction].label : 'Подтвердить'} destructive={lifecycleAction === 'archive'} isPending={lifecycleAction !== null && pendingAction === lifecycleAction} onConfirm={() => { if (lifecycleAction) lifecycle.mutate(lifecycleAction, { onSuccess: () => setLifecycleAction(null) }) }} />
		</div>
	)
}

const ProgramCoverAsset = ({ assetId }: { assetId: string }) => {
	const api = useAdminApi()
	const query = useQuery({
		queryKey: programKeys.cover(assetId),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/media/{asset_id}', {
			params: { path: { asset_id: assetId } },
		})).data,
		staleTime: 60_000,
	})
	const preview = query.data?.variants?.find(variant => variant.role === 'main')?.url ?? query.data?.variants?.[0]?.url
	return preview ? <img src={preview} alt='' className='h-full w-full object-cover' /> : <ImageIcon className='h-9 w-9' />
}
